import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const PriceListModal = ({ isOpen, onClose, onSubmit, priceList = null }) => {
  if (!isOpen) return null;

  const [formData, setFormData] = useState({
    name: '',
    customerId: '',
    description: '',
    validFrom: new Date()?.toISOString()?.split('T')?.[0],
    validUntil: '',
    items: []
  });

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemForm, setItemForm] = useState({
    productId: '',
    discountType: 'percentage',
    discountValue: 0,
    customPrice: '',
    notes: ''
  });

  useEffect(() => {
    fetchFormData();
    if (priceList) {
      loadPriceList();
    }
  }, [priceList]);

  const fetchFormData = async () => {
    try {
      setLoading(true);

      const { data: customersData } = await supabase
        ?.from('customers')
        ?.select('id, name, email')
        ?.eq('status', 'active')
        ?.order('name');

      setCustomers((customersData || [])?.map(c => ({
        value: c?.id,
        label: c?.name,
        description: c?.email
      })));

      const { data: productsData } = await supabase
        ?.from('products')
        ?.select('id, name, sku, price')
        ?.order('name');

      setProducts((productsData || [])?.map(p => ({
        value: p?.id,
        label: p?.name,
        description: `${p?.sku} - Base: $${p?.price}`,
        basePrice: p?.price
      })));
    } catch (error) {
      console.error('Error fetching form data:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPriceList = async () => {
    try {
      const { data, error } = await supabase
        ?.from('customer_price_lists')
        ?.select(`
          *,
          price_list_items(*)
        `)
        ?.eq('id', priceList?.id)
        ?.single();

      if (error) throw error;

      setFormData({
        name: data?.name || '',
        customerId: data?.customer_id || '',
        description: data?.description || '',
        validFrom: data?.valid_from?.split('T')?.[0] || '',
        validUntil: data?.valid_until?.split('T')?.[0] || '',
        items: (data?.price_list_items || [])?.map(item => ({
          productId: item?.product_id,
          discountType: item?.discount_type,
          discountValue: item?.discount_value,
          customPrice: item?.custom_price || '',
          notes: item?.notes || ''
        }))
      });
    } catch (error) {
      console.error('Error loading price list:', error?.message);
    }
  };

  const handleProductSelect = (productId) => {
    const selectedProduct = products?.find(p => p?.value === productId);
    if (selectedProduct) {
      setItemForm(prev => ({
        ...prev,
        productId: productId
      }));
    }
  };

  const handleAddItem = () => {
    if (!itemForm?.productId || itemForm?.discountValue <= 0) {
      alert('Please select a product and enter a valid discount');
      return;
    }

    // Check if product already exists
    if (formData?.items?.some(item => item?.productId === itemForm?.productId)) {
      alert('This product is already in the price list');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev?.items, { ...itemForm }]
    }));

    setItemForm({
      productId: '',
      discountType: 'percentage',
      discountValue: 0,
      customPrice: '',
      notes: ''
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev?.items?.filter((_, i) => i !== index)
    }));
  };

  const calculateDiscountedPrice = (item) => {
    const product = products?.find(p => p?.value === item?.productId);
    if (!product) return 0;

    if (item?.customPrice) {
      return parseFloat(item?.customPrice);
    }

    const basePrice = product?.basePrice || 0;
    if (item?.discountType === 'percentage') {
      return basePrice * (1 - item?.discountValue / 100);
    } else {
      return basePrice - item?.discountValue;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name || !formData?.customerId || formData?.items?.length === 0) {
      alert('Please fill in all required fields and add at least one product');
      return;
    }

    setIsSubmitting(true);
    await onSubmit(formData, priceList?.id);
    setIsSubmitting(false);
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  const getProductName = (productId) => {
    return products?.find(p => p?.value === productId)?.label || 'Unknown Product';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-elevation-5 p-8">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
          <p className="mt-3 text-muted-foreground">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Tag" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {priceList ? 'Edit Price List' : 'Create Price List'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Price List Name"
              placeholder="e.g., VIP Customer Pricing"
              required
              value={formData?.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
            />

            <Select
              label="Customer"
              placeholder="Select customer"
              required
              searchable
              options={customers}
              value={formData?.customerId}
              onChange={(value) => setFormData(prev => ({ ...prev, customerId: value }))}
              disabled={!!priceList}
            />

            <Input
              label="Valid From"
              type="date"
              required
              value={formData?.validFrom}
              onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e?.target?.value }))}
            />

            <Input
              label="Valid Until (Optional)"
              type="date"
              value={formData?.validUntil}
              onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e?.target?.value }))}
              min={formData?.validFrom}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Description</label>
            <textarea
              className="w-full min-h-[80px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y transition-smooth"
              placeholder="Add description for this price list..."
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
            />
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Product Discounts</h3>

            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Select
                  label="Product"
                  placeholder="Select product"
                  searchable
                  options={products}
                  value={itemForm?.productId}
                  onChange={handleProductSelect}
                />

                <Select
                  label="Discount Type"
                  options={[
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'fixed_amount', label: 'Fixed Amount' }
                  ]}
                  value={itemForm?.discountType}
                  onChange={(value) => setItemForm(prev => ({ ...prev, discountType: value }))}
                />

                <Input
                  label={itemForm?.discountType === 'percentage' ? 'Discount %' : 'Discount Amount'}
                  type="number"
                  min="0"
                  step="0.01"
                  value={itemForm?.discountValue}
                  onChange={(e) => setItemForm(prev => ({ ...prev, discountValue: parseFloat(e?.target?.value) || 0 }))}
                />

                <Input
                  label="Custom Price (Optional)"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Override price"
                  value={itemForm?.customPrice}
                  onChange={(e) => setItemForm(prev => ({ ...prev, customPrice: e?.target?.value }))}
                />

                <Input
                  label="Notes (Optional)"
                  placeholder="Add notes"
                  value={itemForm?.notes}
                  onChange={(e) => setItemForm(prev => ({ ...prev, notes: e?.target?.value }))}
                />
              </div>

              <Button
                type="button"
                variant="outline"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddItem}
                className="mt-4"
              >
                Add Product
              </Button>
            </div>

            {formData?.items?.length > 0 && (
              <div className="bg-background rounded-lg border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Product</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Final Price</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Notes</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.items?.map((item, index) => (
                      <tr key={index} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-sm text-foreground">{getProductName(item?.productId)}</td>
                        <td className="px-4 py-3 text-sm text-foreground capitalize">{item?.discountType?.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {item?.discountType === 'percentage' ? `${item?.discountValue}%` : formatCurrency(item?.discountValue)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-success">
                          {formatCurrency(calculateDiscountedPrice(item))}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{item?.notes || '-'}</td>
                        <td className="px-4 py-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleRemoveItem(index)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Check"
              iconPosition="left"
              loading={isSubmitting}
              disabled={isSubmitting || formData?.items?.length === 0}
            >
              {priceList ? 'Update Price List' : 'Create Price List'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceListModal;